import { useEffect, useState } from "react";
import { Loader2, Paperclip, Reply, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const extractEmail = (s) => ((s || "").match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/) || [""])[0];
const splitEmails = (s) => (s || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

function quoted(mail) {
  const lines = (mail.body_text || "").split("\n").map((l) => `> ${l}`).join("\n");
  return `\n\n\nIl ${formatDateTime(mail.received_at)} ${mail.from_addr} ha scritto:\n${lines}`;
}

function ReplyComposer({ mail, offerId, variantId, onSent }) {
  const [to, setTo] = useState(extractEmail(mail.from_addr));
  const [cc, setCc] = useState("");
  const [body, setBody] = useState(quoted(mail));
  const [attachPdf, setAttachPdf] = useState(false);
  const [sending, setSending] = useState(false);
  const [docs, setDocs] = useState(null);
  const [docIds, setDocIds] = useState([]);
  useEffect(() => {
    api.get(`/quote-offers/${offerId}/client-documents`).then(({ data }) => setDocs(data || [])).catch(() => setDocs([]));
  }, [offerId]);
  const toggleDoc = (id) => setDocIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  const subject = /^re:/i.test(mail.subject || "") ? mail.subject : `Re: ${mail.subject || ""}`;

  const send = async () => {
    const toL = splitEmails(to);
    if (!toL.length) { toast.error("Inserisci il destinatario"); return; }
    if (!body.trim()) { toast.error("Scrivi un messaggio"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/quote-offers/${offerId}/variants/${variantId}/send-partner-email`, {
        to: toL, cc: splitEmails(cc), subject, body, attach_pdf: attachPdf, template: "reply", in_reply_to_inbox_id: mail.id, client_attachment_ids: docIds,
      });
      toast.success(`Risposta inviata a ${toL.join(", ")}`);
      onSent?.(data.entry);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-2 rounded-md border border-violet-200 bg-violet-50/40 p-3" data-testid="partner-reply-composer">
      <div className="text-xs text-slate-600">Oggetto: <strong>{subject}</strong></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">A</Label><Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="partner-reply-to" /></div>
        <div><Label className="text-xs">CC</Label><Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="opzionale" data-testid="partner-reply-cc" /></div>
      </div>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="text-sm" autoFocus data-testid="partner-reply-text" />
      <div data-testid="partner-reply-client-docs">
        <Label className="text-xs">Allega documenti dalla scheda cliente</Label>
        {docs === null ? (
          <div className="text-xs text-slate-400 py-1">Caricamento documenti…</div>
        ) : docs.length === 0 ? (
          <div className="text-xs text-slate-500 py-1">Nessun documento nella scheda cliente.</div>
        ) : (
          <div className="mt-1 max-h-36 overflow-y-auto rounded border border-slate-200 bg-white divide-y divide-slate-100">
            {docs.map((d) => (
              <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50" data-testid={`partner-reply-doc-${d.id}`}>
                <Checkbox checked={docIds.includes(d.id)} onCheckedChange={() => toggleDoc(d.id)} />
                <Paperclip className="h-3 w-3 text-slate-400 shrink-0" />
                <span className="truncate flex-1">{d.original_filename}</span>
                {d.kind && <span className="text-[10px] uppercase tracking-wide px-1.5 rounded bg-slate-100 text-slate-600">{String(d.kind).replace(/_/g, " ")}</span>}
                {d.size > 0 && <span className="text-[10px] text-slate-400 tabular-nums">{(d.size / 1024 / 1024).toFixed(1)} MB</span>}
              </label>
            ))}
          </div>
        )}
        {docIds.length > 0 && <div className="text-[11px] text-violet-700 mt-1">{docIds.length} documento/i selezionato/i</div>}
      </div>
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox checked={attachPdf} onCheckedChange={(v) => setAttachPdf(!!v)} data-testid="partner-reply-attach-pdf" /> Allega il PDF della variante
        </label>
        <Button size="sm" onClick={send} disabled={sending} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="partner-reply-send">
          {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Invia risposta
        </Button>
      </div>
    </div>
  );
}

export function PartnerReplyDialog({ emailId, offerId, variantId, onOpenChange, onSent }) {
  const [mail, setMail] = useState(null);
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    if (!emailId) return;
    setMail(null); setReplying(false);
    api.get(`/inbox/${emailId}`)
      .then(({ data }) => setMail(data))
      .catch((e) => { toast.error(formatApiError(e)); onOpenChange(false); });
  }, [emailId, onOpenChange]);

  const download = async (att) => {
    try {
      const r = await api.get(`/inbox/${emailId}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "allegato";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!emailId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="partner-reply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Reply className="h-5 w-5 text-violet-600" /> Risposta del partner</DialogTitle>
          <DialogDescription>{mail ? `${mail.from_addr} · ${formatDateTime(mail.received_at)}` : "Caricamento…"}</DialogDescription>
        </DialogHeader>
        {!mail ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-slate-900" data-testid="partner-reply-subject">{mail.subject}</div>
            <pre className="whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-md p-3 font-sans max-h-64 overflow-y-auto" data-testid="partner-reply-body">
              {mail.body_text || "(nessun testo)"}
            </pre>
            {(mail.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="partner-reply-attachments">
                {mail.attachments.map((att) => (
                  <button key={att.id} type="button" onClick={() => download(att)} data-testid={`partner-reply-att-${att.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">
                    <Paperclip className="h-3 w-3" /> {att.filename}
                  </button>
                ))}
              </div>
            )}
            {offerId && variantId && (replying ? (
              <ReplyComposer mail={mail} offerId={offerId} variantId={variantId}
                onSent={(entry) => { onSent?.(entry); onOpenChange(false); }} />
            ) : (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setReplying(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="partner-reply-open-composer">
                  <Reply className="h-4 w-4 mr-1" /> Rispondi
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PartnerReplyDialog;
